import React from 'react';
import { navSections } from '@/lib/navData';
import type { ApiLayoutProps } from './ApiLayout';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  activeLink: ApiLayoutProps['activeLink'];
  customBreadcrumbs?: BreadcrumbItem[];
}

export default function Breadcrumbs({ activeLink, customBreadcrumbs }: BreadcrumbsProps) {
  const breadcrumbs: BreadcrumbItem[] = customBreadcrumbs || [];

  if (!customBreadcrumbs) {
    // Special handling for overview page to avoid duplication
    if (activeLink === 'overview') {
      breadcrumbs.push({ label: 'Overview' });
    } else {
      // Auto-generate breadcrumbs from navigation structure
      for (const section of navSections) {
        // Check if activeLink is in this section's items
        const item = section.items.find(item => item.id === activeLink);
        if (item) {
          // Only show section and current page for cleaner hierarchy
          breadcrumbs.push(
            { label: section.title, href: '#' },
            { label: item.title }
          );
          break;
        }
      }
    }
  }

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <nav className="breadcrumbs mb-8" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2 text-sm text-gray-600">
        <li>
          <a 
            href="/" 
            className="text-gray-500 hover:text-primary transition-colors duration-200"
          >
            Home
          </a>
        </li>
        {breadcrumbs.map((crumb, index) => (
          <li key={index} className="flex items-center space-x-2">
            <svg 
              className="w-4 h-4 text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
            {crumb.href && crumb.href !== '#' ? (
              <a 
                href={crumb.href} 
                className="text-gray-500 hover:text-primary transition-colors duration-200"
              >
                {crumb.label}
              </a>
            ) : (
              <span className={index === breadcrumbs.length - 1 ? "text-gray-900 font-medium" : "text-gray-500"}>
                {crumb.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
} 